import { Injectable } from '@angular/core';

/**
 * Entrada de cache com dados, timestamp e tempo de expiração
 */
export interface CacheEntry<T> {
  key: string;
  data: T;
  timestamp: number;
  expiresIn: number;
}

/**
 * Configuração do cache
 */
export interface CacheConfig {
  maxSize: number;
  defaultTTL: number; // Time to live em milissegundos
}

/**
 * Serviço para gerenciar cache de resultados de busca
 * Melhora a performance evitando chamadas API duplicadas
 */
@Injectable({
  providedIn: 'root'
})
export class SearchCacheService {
  private cache = new Map<string, CacheEntry<any>>();
  private config: CacheConfig = {
    maxSize: 50,
    defaultTTL: 5 * 60 * 1000 // 5 minutos
  };

  /**
   * Armazena dados no cache
   * @param key Chave única para identificar os dados
   * @param data Dados a serem armazenados
   * @param ttl Tempo de vida opcional (em ms)
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Remove entradas expiradas se cache estiver cheio
    if (this.cache.size >= this.config.maxSize) {
      this.evictExpired();
    }

    const entry: CacheEntry<T> = {
      key,
      data,
      timestamp: Date.now(),
      expiresIn: ttl || this.config.defaultTTL
    };

    this.cache.set(key, entry);
  }

  /**
   * Recupera dados do cache
   * @param key Chave dos dados
   * @returns Dados armazenados ou null se não existir ou expirado
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Verifica se expirou
    if (Date.now() - entry.timestamp > entry.expiresIn) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * Verifica se uma chave existe no cache e não expirou
   * @param key Chave a verificar
   * @returns true se existe e não expirou
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Remove uma entrada específica do cache
   * @param key Chave a remover
   */
  remove(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Limpa todo o cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Gera uma chave de cache a partir de parâmetros
   * @param params Parâmetros da busca
   * @returns Chave única como string
   */
  generateKey(params: any): string {
    return JSON.stringify(params);
  }

  /**
   * Retorna o tamanho atual do cache
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Remove entradas expiradas do cache
   */
  private evictExpired(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.expiresIn) {
        this.cache.delete(key);
      }
    }
  }
}
